import { useEffect, useMemo, useRef, useState } from "react";
import { format } from "date-fns";
import { ArrowDown, ArrowUp, CalendarClock, CircleHelp, Lock, Plus, X } from "lucide-react";
import { toast } from "sonner";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "../../../components/ui/dialog";
import { Button } from "../../../components/ui/button";
import { Calendar } from "../../../components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "../../../components/ui/popover";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../../components/ui/select";
import { BASIC_POLL_STATUSES, BASIC_POLL_VOTE_TYPES, resolveBasicPollVoteType } from "../../../lib/basic-polls/constants";
import { createBasicPoll, updateBasicPoll } from "../../../lib/data/basicPolls";
import {
  isDeadlineInPast,
  resolveSelectableCount,
  validateMaxSelections,
} from "../../../lib/basic-polls/validate-poll";
import { coerceDate } from "../../../lib/time";
import { resolveBasicPollDeadline } from "../../../lib/voting-deadlines";
import {
  DEFAULT_VOTE_ANONYMIZATION,
  DEFAULT_VOTE_VISIBILITY,
  VOTE_ANONYMIZATION,
  VOTE_VISIBILITY_OPTIONS,
  resolveVoteAnonymization,
  resolveHideVoterIdentitiesForVisibility,
  resolveVoteVisibility,
} from "../../../lib/vote-visibility";
import { PollMarkdownContent } from "../../../components/polls/poll-markdown-content";
import { VotePrivacySettings } from "../../../components/polls/vote-privacy-settings";
import { createReminderSettingsSnapshot } from "../../reminders/reminder-settings";
import {
  PollNotificationSettings,
} from "../../reminders/PollNotificationSettings";
import { pollReminderSummary } from "../../reminders/poll-notification-settings";
import { QuestingGroupSelect } from "../../scheduler/components/questing-group-select";

const ERROR_ALERT_ID = "create-group-poll-error";
const EMPTY_GROUP_OPTIONS = Object.freeze([]);

function createDefaultOptions() {
  return [
    { id: `option-${crypto.randomUUID()}`, label: "", note: "" },
    { id: `option-${crypto.randomUUID()}`, label: "", note: "" },
  ];
}

function toDateTimeLocalValue(dateValue) {
  const date = coerceDate(dateValue);
  if (!date) return "";
  const offset = date.getTimezoneOffset();
  const local = new Date(date.getTime() - offset * 60 * 1000);
  return local.toISOString().slice(0, 16);
}

function parseLocalDateTime(value) {
  if (!value) return null;
  const parsed = new Date(value);
  return Number.isNaN(parsed.getTime()) ? null : parsed;
}

function normalizeInitialOptions(initialPoll) {
  const rawOptions = Array.isArray(initialPoll?.options) ? initialPoll.options : [];
  const sorted = [...rawOptions].sort((left, right) => {
    const leftOrder = Number.isFinite(left?.order) ? left.order : Number.MAX_SAFE_INTEGER;
    const rightOrder = Number.isFinite(right?.order) ? right.order : Number.MAX_SAFE_INTEGER;
    return leftOrder - rightOrder;
  });
  const normalized = sorted.map((option, index) => ({
    id: option?.id || `option-${crypto.randomUUID()}`,
    label: String(option?.label || ""),
    note: String(option?.note || ""),
    order: index,
  }));
  if (normalized.length >= 2) return normalized;
  if (normalized.length === 1) {
    return [...normalized, { id: `option-${crypto.randomUUID()}`, label: "", note: "" }];
  }
  return createDefaultOptions();
}

function buildInitialState(selectedGroupId = null, initialPoll = null, selectedGroup = null) {
  const settings = initialPoll?.settings || {};
  const voteType = resolveBasicPollVoteType(settings.voteType);
  const allowMultiple = voteType === BASIC_POLL_VOTE_TYPES.MULTIPLE_CHOICE && settings.allowMultiple === true;
  const allowWriteIn = voteType === BASIC_POLL_VOTE_TYPES.MULTIPLE_CHOICE && settings.allowWriteIn === true;
  const maxSelections =
    allowMultiple && Number.isFinite(settings.maxSelections)
      ? String(Math.max(1, Number(settings.maxSelections)))
      : "";
  const deadlineAtLocal = toDateTimeLocalValue(resolveBasicPollDeadline(initialPoll || {}));
  const voteVisibility = resolveVoteVisibility(
    initialPoll?.voteVisibility || DEFAULT_VOTE_VISIBILITY
  );
  const voteAnonymization = resolveVoteAnonymization(
    initialPoll?.voteAnonymization || DEFAULT_VOTE_ANONYMIZATION
  );
  return {
    selectedGroupId,
    title: String(initialPoll?.title || ""),
    description: String(initialPoll?.description || ""),
    descriptionTab: "write",
    voteType,
    allowMultiple,
    maxSelections,
    allowWriteIn,
    voteVisibility,
    voteAnonymization,
    hideVoterIdentities: resolveHideVoterIdentitiesForVisibility(
      initialPoll?.hideVoterIdentities === true,
      voteVisibility
    ),
    deadlineAtLocal,
    reminders: createReminderSettingsSnapshot(
      initialPoll?.reminders ?? selectedGroup?.reminders,
      { discordLinked: Boolean(selectedGroup?.discord?.channelId) }
    ),
    options: normalizeInitialOptions(initialPoll),
    noteEditor: { optionId: null, tab: "write", value: "" },
  };
}

export function CreateGroupPollModal({
  open,
  onOpenChange,
  groupId,
  groupName,
  groupOptions = EMPTY_GROUP_OPTIONS,
  initialGroupId = null,
  creatorId,
  mode = "create",
  initialPoll = null,
  onCreated,
  onEdited,
}) {
  const isEditMode = mode === "edit";
  const editPollId = initialPoll?.pollId || initialPoll?.id || null;

  // hasVotes drives the edit-after-votes STRUCTURAL lock. The dashboard
  // enriches polls with votedCount / voterIds (buildDashboardPollSummary); if
  // those are absent we fall back to counting voter ids defensively.
  const initialVoterIds = useMemo(
    () => (Array.isArray(initialPoll?.voterIds) ? initialPoll.voterIds : []),
    [initialPoll]
  );
  const hasVotes =
    isEditMode && ((Number(initialPoll?.votedCount) || 0) > 0 || initialVoterIds.length > 0);
  // Structural fields (option ids/order/labels + voteType/allowMultiple/
  // maxSelections/allowWriteIn) are locked once votes exist — changing them
  // would silently rewrite the meaning of already-cast votes. Title,
  // description, option NOTES, deadline, and privacy stay editable.
  const structuralLocked = hasVotes;

  // Mirror the scheduler flow's anonymity lock (CreateSchedulerPage): once a
  // non-creator has voted under "Anonymous for everyone", that anonymity
  // promise can't be retroactively revoked. Computed from the PERSISTED poll
  // (initialPoll), not the editable draft, so toggling the draft control can't
  // relax the lock before save.
  const initialCreatorId = String(initialPoll?.creatorId || "").trim();
  const nonCreatorHasVoted = initialVoterIds.some(
    (voterId) => String(voterId || "").trim() && String(voterId).trim() !== initialCreatorId
  );
  const voteAnonymizationLocked =
    isEditMode &&
    resolveVoteAnonymization(initialPoll?.voteAnonymization) === VOTE_ANONYMIZATION.ALL_PARTICIPANTS &&
    nonCreatorHasVoted;

  const selectableGroups = useMemo(() => {
    if (groupId) {
      const supplied = (Array.isArray(groupOptions) ? groupOptions : [])
        .find((group) => group?.id === groupId);
      return [supplied || { id: groupId, name: groupName || "Questing group" }];
    }
    return Array.isArray(groupOptions) ? groupOptions.filter((group) => group?.id) : [];
  }, [groupId, groupName, groupOptions]);
  const initialSelectedGroupId = useMemo(() => {
    if (groupId) return groupId;
    if (
      initialGroupId &&
      selectableGroups.some((group) => group.id === initialGroupId)
    ) {
      return initialGroupId;
    }
    return selectableGroups[0]?.id || null;
  }, [groupId, initialGroupId, selectableGroups]);
  const initialSelectedGroup = useMemo(
    () => selectableGroups.find((group) => group.id === initialSelectedGroupId) || null,
    [initialSelectedGroupId, selectableGroups]
  );

  const [state, setState] = useState(() =>
    buildInitialState(initialSelectedGroupId, initialPoll, initialSelectedGroup)
  );
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [errorField, setErrorField] = useState(null);
  const [deadlineEditorOpen, setDeadlineEditorOpen] = useState(false);
  const [customizationOpen, setCustomizationOpen] = useState(false);
  const [advancedSettingsExpanded, setAdvancedSettingsExpanded] = useState(false);
  const activeGroupId = groupId || state.selectedGroupId || null;
  const activeGroupName =
    groupName ||
    selectableGroups.find((group) => group.id === activeGroupId)?.name ||
    "Questing group";
  const activeGroup = selectableGroups.find((group) => group.id === activeGroupId) || null;
  const inheritedReminderSettings = createReminderSettingsSnapshot(activeGroup?.reminders, {
    discordLinked: Boolean(activeGroup?.discord?.channelId),
  });

  const errorAlertRef = useRef(null);

  useEffect(() => {
    if (!open) return;
    setState(buildInitialState(initialSelectedGroupId, initialPoll, initialSelectedGroup));
    setSaving(false);
    setError(null);
    setErrorField(null);
    setAdvancedSettingsExpanded(false);
  }, [initialPoll, initialSelectedGroup, initialSelectedGroupId, open]);

  // Move focus to the error summary so it is announced + reachable (WCAG
  // 2.4.7 / 3.3.1 / 4.1.3). role="alert" handles the announcement even without
  // focus, but focusing the summary guarantees screen-reader users land on it.
  useEffect(() => {
    if (!error) return;
    const node = errorAlertRef.current;
    if (node && typeof node.focus === "function") node.focus();
  }, [error]);

  const selectedOption = useMemo(
    () => state.options.find((option) => option.id === state.noteEditor.optionId) || null,
    [state.noteEditor.optionId, state.options]
  );

  function updateOption(optionId, updates) {
    setState((previous) => ({
      ...previous,
      options: previous.options.map((option) =>
        option.id === optionId ? { ...option, ...updates } : option
      ),
    }));
  }

  function moveOption(optionId, direction) {
    setState((previous) => {
      const currentIndex = previous.options.findIndex((option) => option.id === optionId);
      if (currentIndex < 0) return previous;
      const nextIndex = direction === "up" ? currentIndex - 1 : currentIndex + 1;
      if (nextIndex < 0 || nextIndex >= previous.options.length) return previous;

      const nextOptions = [...previous.options];
      const [moved] = nextOptions.splice(currentIndex, 1);
      nextOptions.splice(nextIndex, 0, moved);
      return { ...previous, options: nextOptions };
    });
  }

  function addOption() {
    setState((previous) => ({
      ...previous,
      options: [...previous.options, { id: `option-${crypto.randomUUID()}`, label: "", note: "" }],
    }));
  }

  function removeOption(optionId) {
    setState((previous) => {
      if (previous.options.length <= 2) return previous;
      return {
        ...previous,
        options: previous.options.filter((option) => option.id !== optionId),
      };
    });
  }

  function updateVotingSetup(value) {
    setState((previous) => ({
      ...previous,
      // Preserve allowMultiple / allowWriteIn / maxSelections in draft when
      // switching MC -> ranked so switching back restores the prior config
      // (these only apply while voteType === MULTIPLE_CHOICE, gated in render
      // + payload). Previously switching wiped them and they couldn't be
      // restored.
      voteType: resolveBasicPollVoteType(value),
    }));
  }

  function updateDeadlineDate(nextDate) {
    if (!nextDate) return;
    setState((previous) => {
      const current = parseLocalDateTime(previous.deadlineAtLocal) || new Date();
      const merged = new Date(current);
      merged.setFullYear(nextDate.getFullYear(), nextDate.getMonth(), nextDate.getDate());
      return { ...previous, deadlineAtLocal: toDateTimeLocalValue(merged) };
    });
  }

  function updateDeadlineTime(value) {
    if (!value || !/^\d{2}:\d{2}$/.test(value)) return;
    const [hoursText, minutesText] = value.split(":");
    const hours = Number(hoursText);
    const minutes = Number(minutesText);
    if (!Number.isFinite(hours) || !Number.isFinite(minutes)) return;
    setState((previous) => {
      const current = parseLocalDateTime(previous.deadlineAtLocal) || new Date();
      const merged = new Date(current);
      merged.setHours(hours, minutes, 0, 0);
      return { ...previous, deadlineAtLocal: toDateTimeLocalValue(merged) };
    });
  }

  function clearDeadline() {
    setState((previous) => ({ ...previous, deadlineAtLocal: "" }));
  }

  const filledOptionCount = useMemo(
    () =>
      state.options.filter((option) => String(option?.label || "").trim().length > 0).length,
    [state.options]
  );
  const hasDuplicateOptionLabel = useMemo(() => {
    const filled = state.options
      .map((option) => String(option?.label || "").trim().toLowerCase())
      .filter((label) => label.length > 0);
    return filled.length > 0 && new Set(filled).size < filled.length;
  }, [state.options]);
  const voteTypeLabel =
    state.voteType === BASIC_POLL_VOTE_TYPES.RANKED_CHOICE ? "Ranked choice" : "Multiple choice";
  const votingSetupValue =
    state.voteType === BASIC_POLL_VOTE_TYPES.RANKED_CHOICE
      ? BASIC_POLL_VOTE_TYPES.RANKED_CHOICE
      : BASIC_POLL_VOTE_TYPES.MULTIPLE_CHOICE;
  const hasMaxSelectionCustomization =
    state.voteType === BASIC_POLL_VOTE_TYPES.MULTIPLE_CHOICE &&
    state.allowMultiple &&
    String(state.maxSelections || "").trim().length > 0;
  const deadlineDate = useMemo(() => parseLocalDateTime(state.deadlineAtLocal), [state.deadlineAtLocal]);
  const voteVisibilityLabel =
    VOTE_VISIBILITY_OPTIONS.find((option) => option.value === state.voteVisibility)?.label ||
    "Vote visibility";
  const deadlineTimeValue = deadlineDate
    ? `${String(deadlineDate.getHours()).padStart(2, "0")}:${String(
        deadlineDate.getMinutes()
      ).padStart(2, "0")}`
    : "19:00";
  const deadlineChipLabel = deadlineDate
    ? format(deadlineDate, "MMM d, yyyy h:mm a")
    : "Add deadline";

  const todayAtStartOfDay = useMemo(() => {
    const now = new Date();
    now.setHours(0, 0, 0, 0);
    return now;
  }, []);

  function failValidation(message, field) {
    setError(message);
    setErrorField(field || null);
  }

  async function handleSubmitPoll() {
    if (saving) return;
    if (!activeGroupId) {
      failValidation("Select a questing group.", null);
      return;
    }
    if (isEditMode && !editPollId) {
      failValidation("This poll can no longer be edited.", null);
      return;
    }
    const normalizedTitle = String(state.title || "").trim();
    if (!normalizedTitle) {
      failValidation("Title is required.", "title");
      return;
    }

    const normalizedOptions = state.options
      .map((option, index) => ({
        id: option.id || `option-${index + 1}`,
        label: String(option.label || "").trim(),
        order: index,
        note: String(option.note || "").trim(),
      }))
      .filter((option) => option.label.length > 0);

    if (normalizedOptions.length < 2) {
      failValidation("Add at least two options.", "options");
      return;
    }

    const parsedMaxSelections =
      state.voteType === BASIC_POLL_VOTE_TYPES.MULTIPLE_CHOICE && state.allowMultiple && state.maxSelections
        ? Number(state.maxSelections)
        : null;
    const maxSelectionsResult = validateMaxSelections({
      voteType: state.voteType,
      allowMultiple: state.allowMultiple,
      allowWriteIn: state.allowWriteIn,
      optionCount: normalizedOptions.length,
      maxSelections: parsedMaxSelections,
    });
    if (!maxSelectionsResult.valid) {
      failValidation(maxSelectionsResult.message, "maxSelections");
      return;
    }

    const deadlineAt = state.deadlineAtLocal ? new Date(state.deadlineAtLocal) : null;
    if (deadlineAt && isDeadlineInPast(deadlineAt)) {
      failValidation("Deadline must be in the future.", "deadline");
      return;
    }

    setSaving(true);
    setError(null);
    setErrorField(null);
    try {
      const normalizedVoteVisibility = resolveVoteVisibility(state.voteVisibility);
      const payload = {
        title: normalizedTitle,
        description: String(state.description || "").trim(),
        options: normalizedOptions,
        voteVisibility: normalizedVoteVisibility,
        // When anonymization is locked (someone already voted anonymously), force
        // ALL_PARTICIPANTS in the payload — a backend-level guarantee that doesn't
        // rely solely on the disabled radio (defense-in-depth, mirrors the scheduler).
        voteAnonymization: voteAnonymizationLocked
          ? VOTE_ANONYMIZATION.ALL_PARTICIPANTS
          : resolveVoteAnonymization(state.voteAnonymization),
        hideVoterIdentities: resolveHideVoterIdentitiesForVisibility(
          state.hideVoterIdentities === true,
          normalizedVoteVisibility
        ),
        settings: {
          voteType: state.voteType,
          allowMultiple: state.voteType === BASIC_POLL_VOTE_TYPES.MULTIPLE_CHOICE && state.allowMultiple,
          maxSelections:
            state.voteType === BASIC_POLL_VOTE_TYPES.MULTIPLE_CHOICE &&
            state.allowMultiple &&
            parsedMaxSelections
              ? parsedMaxSelections
              : null,
          allowWriteIn: state.voteType === BASIC_POLL_VOTE_TYPES.MULTIPLE_CHOICE && state.allowWriteIn,
          deadlineAt: deadlineAt,
        },
        reminders: state.reminders,
      };
      if (isEditMode) {
        await updateBasicPoll(activeGroupId, editPollId, payload);
      } else {
        const pollId = await createBasicPoll(
          activeGroupId,
          {
            ...payload,
            creatorId: creatorId || null,
            status: BASIC_POLL_STATUSES.OPEN,
          },
          { useServer: true }
        );
        if (onCreated) onCreated(pollId, activeGroupId);
      }

      toast.success(isEditMode ? "Poll updated" : "Poll created");
      onOpenChange(false);
      if (isEditMode) {
        if (onEdited) onEdited(editPollId, activeGroupId);
      }
    } catch (nextError) {
      failValidation(
        nextError?.message || `Failed to ${isEditMode ? "update" : "create"} poll.`,
        null
      );
    } finally {
      setSaving(false);
    }
  }

  const fieldDescribedBy = (field) =>
    error && errorField === field ? ERROR_ALERT_ID : undefined;
  const fieldIsInvalid = (field) => Boolean(error && errorField === field);

  return (
    <>
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent className="max-w-2xl">
          <>
            <DialogHeader>
              <DialogTitle>{isEditMode ? "Edit poll" : "Create poll"}</DialogTitle>
              <DialogDescription>
                {isEditMode
                  ? `Update poll details for "${activeGroupName}".`
                  : `Create a standalone poll for "${activeGroupName}".`}
              </DialogDescription>
            </DialogHeader>

            {structuralLocked ? (
              <div
                role="status"
                className="mt-3 flex items-start gap-2 rounded-xl border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-800 dark:border-amber-700/70 dark:bg-amber-900/30 dark:text-amber-200"
              >
                <Lock className="mt-0.5 h-4 w-4 shrink-0" aria-hidden="true" />
                <span>
                  Votes have already been cast — options and vote type are locked to keep existing
                  votes valid. You can still edit the title, description, option notes, deadline, and
                  privacy.
                </span>
              </div>
            ) : null}

            {error ? (
              <p
                id={ERROR_ALERT_ID}
                ref={errorAlertRef}
                role="alert"
                tabIndex={-1}
                className="mt-3 rounded-lg border border-rose-300 bg-rose-50 px-3 py-2 text-sm text-rose-700 outline-none focus-visible:ring-2 focus-visible:ring-ring dark:border-rose-700 dark:bg-rose-950/40 dark:text-rose-400"
              >
                {error}
              </p>
            ) : null}

            <div className="mt-3 flex flex-wrap items-center gap-2">
              <span className="rounded-full border border-border px-2.5 py-1 text-[11px] font-semibold text-muted-foreground">
                {activeGroupName}
              </span>
              <span className="rounded-full border border-border px-2.5 py-1 text-[11px] font-semibold text-muted-foreground">
                {voteTypeLabel}
              </span>
              <span className="rounded-full border border-border px-2.5 py-1 text-[11px] font-semibold text-muted-foreground">
                {filledOptionCount} option{filledOptionCount === 1 ? "" : "s"}
              </span>
              <span className="rounded-full border border-border px-2.5 py-1 text-[11px] font-semibold text-muted-foreground">
                {voteVisibilityLabel}
              </span>
              <Popover open={deadlineEditorOpen} onOpenChange={setDeadlineEditorOpen}>
                <PopoverTrigger asChild>
                  <button
                    type="button"
                    className="inline-flex items-center gap-1 rounded-full border border-border bg-card px-2.5 py-1 text-[11px] font-semibold text-card-foreground transition-colors hover:bg-muted focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
                  >
                    <CalendarClock className="h-3.5 w-3.5" />
                    {deadlineChipLabel}
                  </button>
                </PopoverTrigger>
                <PopoverContent align="start" className="w-[min(22rem,calc(100vw-2rem))] p-3">
                  <div className="space-y-2">
                    <div className="flex items-center justify-between">
                      <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                        Deadline
                      </p>
                      {deadlineDate ? (
                        <button
                          type="button"
                          onClick={clearDeadline}
                          className="text-xs font-semibold text-muted-foreground transition-colors hover:text-card-foreground"
                        >
                          Clear
                        </button>
                      ) : null}
                    </div>
                    <div className="flex justify-center rounded-xl border border-border">
                      <Calendar
                        mode="single"
                        selected={deadlineDate || undefined}
                        onSelect={updateDeadlineDate}
                        disabled={(date) => date < todayAtStartOfDay}
                      />
                    </div>
                    <div className="space-y-1">
                      <label
                        htmlFor="poll-deadline-time"
                        className="text-xs font-semibold uppercase tracking-wide text-muted-foreground"
                      >
                        Time
                      </label>
                      <input
                        id="poll-deadline-time"
                        type="time"
                        value={deadlineTimeValue}
                        onChange={(event) => updateDeadlineTime(event.target.value)}
                        aria-invalid={fieldIsInvalid("deadline")}
                        aria-describedby={fieldDescribedBy("deadline")}
                        className="w-full rounded-xl border border-input bg-card px-3 py-2 text-sm text-card-foreground outline-none transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                      />
                    </div>
                  </div>
                </PopoverContent>
              </Popover>
            </div>

            <div className="mt-4 space-y-4">
              {!groupId && !isEditMode ? (
                selectableGroups.length > 0 ? (
                  <QuestingGroupSelect
                    groups={selectableGroups}
                    selectedId={state.selectedGroupId}
                    onChange={(value) => {
                      const selectedGroupId = value === "none" ? null : value;
                      const selectedGroup = selectableGroups.find(
                        (group) => group.id === selectedGroupId
                      ) || null;
                      setState((previous) => ({
                        ...previous,
                        selectedGroupId,
                        reminders: createReminderSettingsSnapshot(selectedGroup?.reminders, {
                          discordLinked: Boolean(selectedGroup?.discord?.channelId),
                        }),
                      }));
                    }}
                    label="Questing group"
                    placeholder="Select a questing group"
                    showNoneOption={false}
                    helperText={null}
                  />
                ) : (
                  <p className="rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-700 dark:border-amber-700/70 dark:bg-amber-900/30 dark:text-amber-200">
                    You need manager access to at least one questing group to create a general poll.
                  </p>
                )
              ) : null}

              <div className="space-y-2">
                <label
                  htmlFor="poll-title"
                  className="block text-sm font-medium text-card-foreground"
                >
                  Title
                </label>
                <input
                  id="poll-title"
                  value={state.title}
                  onChange={(event) =>
                    setState((previous) => ({ ...previous, title: event.target.value }))
                  }
                  placeholder="What should we decide?"
                  aria-invalid={fieldIsInvalid("title")}
                  aria-describedby={fieldDescribedBy("title")}
                  className="w-full rounded-xl border border-input bg-card px-3 py-2.5 text-sm text-card-foreground outline-none transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                />
              </div>

              <div className="space-y-2">
                <div className="flex items-center justify-between gap-2">
                  <label
                    htmlFor="poll-description"
                    className="block text-sm font-medium text-card-foreground"
                  >
                    Description
                  </label>
                  <div className="inline-flex rounded-full border border-border bg-card p-0.5 text-xs">
                    <button
                      type="button"
                      onClick={() =>
                        setState((previous) => ({ ...previous, descriptionTab: "write" }))
                      }
                      aria-pressed={state.descriptionTab === "write"}
                      className={`rounded-full px-3 py-1 font-semibold focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 ${
                        state.descriptionTab === "write"
                          ? "bg-brand-primary text-white"
                          : "text-muted-foreground hover:text-card-foreground"
                      }`}
                    >
                      Write
                    </button>
                    <button
                      type="button"
                      onClick={() =>
                        setState((previous) => ({ ...previous, descriptionTab: "preview" }))
                      }
                      aria-pressed={state.descriptionTab === "preview"}
                      className={`rounded-full px-3 py-1 font-semibold focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 ${
                        state.descriptionTab === "preview"
                          ? "bg-brand-primary text-white"
                          : "text-muted-foreground hover:text-card-foreground"
                      }`}
                    >
                      Preview
                    </button>
                  </div>
                </div>
                {state.descriptionTab === "write" ? (
                  <textarea
                    id="poll-description"
                    value={state.description}
                    onChange={(event) =>
                      setState((previous) => ({ ...previous, description: event.target.value }))
                    }
                    rows={4}
                    className="w-full rounded-xl border border-input bg-card px-3 py-2.5 text-sm text-card-foreground outline-none transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                  />
                ) : (
                  <PollMarkdownContent
                    content={state.description}
                    fallback="_No description_"
                    className="rounded-xl border border-border bg-card px-3 py-2"
                  />
                )}
              </div>

              <div className="rounded-2xl border border-border bg-muted p-3">
                <div className="flex flex-wrap items-center justify-between gap-2">
                  <h3 className="text-sm font-semibold text-card-foreground">Options</h3>
                  <div className="flex flex-wrap items-center gap-2">
                    <Select
                      value={votingSetupValue}
                      onValueChange={updateVotingSetup}
                      disabled={structuralLocked}
                    >
                      <SelectTrigger
                        aria-label="Vote type"
                        disabled={structuralLocked}
                        aria-disabled={structuralLocked}
                        className="h-8 w-[13rem] rounded-full border-input bg-card px-3 text-xs"
                      >
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value={BASIC_POLL_VOTE_TYPES.MULTIPLE_CHOICE}>Multiple choice</SelectItem>
                        <SelectItem value={BASIC_POLL_VOTE_TYPES.RANKED_CHOICE}>Ranked choice</SelectItem>
                      </SelectContent>
                    </Select>
                    <Popover open={customizationOpen} onOpenChange={setCustomizationOpen}>
                      <PopoverTrigger asChild>
                        <button
                          type="button"
                          className="inline-flex items-center rounded-full border border-border bg-card px-3 py-1 text-xs font-semibold text-card-foreground transition-colors hover:bg-muted focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
                        >
                          Settings
                        </button>
                      </PopoverTrigger>
                      <PopoverContent align="end" className="w-72 p-3">
                        {state.voteType === BASIC_POLL_VOTE_TYPES.MULTIPLE_CHOICE ? (
                          <div className="space-y-3">
                            <label className="flex items-center gap-2 text-sm text-card-foreground">
                              <input
                                type="checkbox"
                                className="accent-brand-primary"
                                checked={state.allowMultiple}
                                onChange={(event) =>
                                  setState((previous) => ({
                                    ...previous,
                                    allowMultiple: event.target.checked,
                                    maxSelections: event.target.checked ? previous.maxSelections : "",
                                  }))
                                }
                                disabled={structuralLocked}
                                aria-disabled={structuralLocked}
                              />
                              Allow multiple
                            </label>
                            {state.allowMultiple ? (
                              <div className="space-y-1">
                                <label
                                  htmlFor="poll-max-selections"
                                  className="block text-xs font-semibold uppercase tracking-wide text-muted-foreground"
                                >
                                  Max selections
                                </label>
                                <input
                                  id="poll-max-selections"
                                  type="number"
                                  min={2}
                                  value={state.maxSelections}
                                  onChange={(event) =>
                                    setState((previous) => ({
                                      ...previous,
                                      maxSelections: event.target.value,
                                    }))
                                  }
                                  disabled={structuralLocked}
                                  aria-disabled={structuralLocked}
                                  aria-invalid={fieldIsInvalid("maxSelections")}
                                  aria-describedby={fieldDescribedBy("maxSelections")}
                                  className="w-full rounded-xl border border-input bg-card px-3 py-2 text-sm text-card-foreground outline-none transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring disabled:opacity-60"
                                />
                                <p className="text-[11px] text-muted-foreground">
                                  A whole number from 2 to{" "}
                                  {resolveSelectableCount({
                                    optionCount: filledOptionCount,
                                    allowWriteIn: state.allowWriteIn,
                                  })}
                                  {state.allowWriteIn ? " (options plus write-in)" : ""}. Use
                                  single-choice (turn &quot;Allow multiple&quot; off) for one selection.
                                </p>
                              </div>
                            ) : null}
                          </div>
                        ) : (
                          <p className="text-xs text-muted-foreground">
                            Ranked choice has no extra customization settings.
                          </p>
                        )}
                      </PopoverContent>
                    </Popover>
                    {state.voteType === BASIC_POLL_VOTE_TYPES.RANKED_CHOICE ? (
                      <span
                        className="inline-flex h-8 items-center justify-center text-muted-foreground"
                        title="Ranked choice does not support multi-select or write-in options."
                        aria-label="Ranked choice help"
                      >
                        <CircleHelp className="h-4 w-4" />
                      </span>
                    ) : null}
                  </div>
                </div>
                <div className="mt-2 flex flex-wrap items-center gap-2">
                  {state.voteType === BASIC_POLL_VOTE_TYPES.MULTIPLE_CHOICE && state.allowMultiple ? (
                    <div className="group relative">
                      <span className="inline-flex items-center rounded-full border border-border bg-card px-3 py-1 pr-7 text-xs font-semibold text-card-foreground">
                        Allow multiple
                      </span>
                      <button
                        type="button"
                        onClick={() =>
                          setState((previous) => ({
                            ...previous,
                            allowMultiple: false,
                            maxSelections: "",
                          }))
                        }
                        disabled={structuralLocked}
                        aria-disabled={structuralLocked}
                        className="absolute right-1 top-1/2 inline-flex h-6 w-6 shrink-0 -translate-y-1/2 items-center justify-center rounded-full text-muted-foreground hover:bg-muted hover:text-card-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring disabled:opacity-0"
                        aria-label="Remove allow multiple customization"
                      >
                        <X className="h-3.5 w-3.5" aria-hidden="true" />
                      </button>
                    </div>
                  ) : null}
                  {hasMaxSelectionCustomization ? (
                    <div className="group relative">
                      <span className="inline-flex items-center rounded-full border border-border bg-card px-3 py-1 pr-7 text-xs font-semibold text-card-foreground">
                        Max selections: {state.maxSelections}
                      </span>
                      <button
                        type="button"
                        onClick={() =>
                          setState((previous) => ({
                            ...previous,
                            maxSelections: "",
                          }))
                        }
                        disabled={structuralLocked}
                        aria-disabled={structuralLocked}
                        className="absolute right-1 top-1/2 inline-flex h-6 w-6 shrink-0 -translate-y-1/2 items-center justify-center rounded-full text-muted-foreground hover:bg-muted hover:text-card-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring disabled:opacity-0"
                        aria-label="Remove max selections customization"
                      >
                        <X className="h-3.5 w-3.5" aria-hidden="true" />
                      </button>
                    </div>
                  ) : null}
                </div>

                <div className="mt-3 space-y-2">
                  {state.options.map((option, index) => {
                    const optionInputId = `poll-option-${option.id}`;
                    return (
                      <div
                        key={option.id}
                        className="rounded-xl border border-border bg-card p-3"
                      >
                        <div className="flex items-center gap-2">
                          <span
                            aria-hidden="true"
                            className="inline-flex h-6 min-w-6 items-center justify-center rounded-full border border-border bg-muted px-1 text-[11px] font-semibold text-card-foreground"
                          >
                            {index + 1}
                          </span>
                          <label htmlFor={optionInputId} className="sr-only">
                            {`Option ${index + 1}`}
                          </label>
                          <input
                            id={optionInputId}
                            value={option.label}
                            onChange={(event) => updateOption(option.id, { label: event.target.value })}
                            placeholder={`Option ${index + 1}`}
                            disabled={structuralLocked}
                            aria-disabled={structuralLocked}
                            aria-invalid={index === 0 && fieldIsInvalid("options")}
                            aria-describedby={index === 0 ? fieldDescribedBy("options") : undefined}
                            className="min-w-[180px] flex-1 rounded-lg border border-input bg-card px-3 py-2 text-sm text-card-foreground outline-none transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring disabled:cursor-not-allowed disabled:opacity-70"
                          />
                        </div>
                        <div className="mt-2 flex flex-wrap items-center gap-1.5 pl-8">
                          <button
                            type="button"
                            onClick={() => moveOption(option.id, "up")}
                            disabled={index === 0 || structuralLocked}
                            aria-disabled={index === 0 || structuralLocked}
                            className="inline-flex min-h-[32px] items-center gap-1 rounded-md border border-border px-2 py-1 text-xs text-muted-foreground transition-colors hover:bg-muted focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring disabled:opacity-40"
                          >
                            <ArrowUp className="h-3 w-3" />
                            Up
                          </button>
                          <button
                            type="button"
                            onClick={() => moveOption(option.id, "down")}
                            disabled={index === state.options.length - 1 || structuralLocked}
                            aria-disabled={index === state.options.length - 1 || structuralLocked}
                            className="inline-flex min-h-[32px] items-center gap-1 rounded-md border border-border px-2 py-1 text-xs text-muted-foreground transition-colors hover:bg-muted focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring disabled:opacity-40"
                          >
                            <ArrowDown className="h-3 w-3" />
                            Down
                          </button>
                          <button
                            type="button"
                            onClick={() =>
                              setState((previous) => ({
                                ...previous,
                                noteEditor: { optionId: option.id, tab: "write", value: option.note || "" },
                              }))
                            }
                            className="min-h-[32px] rounded-md border border-border px-2 py-1 text-xs text-muted-foreground transition-colors hover:bg-muted focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                          >
                            {option.note ? "Edit note" : "Add note"}
                          </button>
                          <button
                            type="button"
                            onClick={() => removeOption(option.id)}
                            disabled={state.options.length <= 2 || structuralLocked}
                            aria-disabled={state.options.length <= 2 || structuralLocked}
                            className="rounded-md border border-rose-300 px-2 py-1 text-xs text-rose-700 transition-colors hover:bg-rose-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring disabled:opacity-40 dark:border-rose-600 dark:text-rose-300 dark:hover:bg-rose-950/30"
                          >
                            Remove
                          </button>
                        </div>
                      </div>
                    );
                  })}
                </div>

                {hasDuplicateOptionLabel ? (
                  <p
                    role="status"
                    aria-live="polite"
                    className="mt-2 text-[11px] text-amber-700 dark:text-amber-200"
                  >
                    Two options share the same label — voters may find them hard to tell apart.
                  </p>
                ) : null}

                <div className="mt-2 flex items-center justify-center gap-2 text-xs font-semibold text-muted-foreground">
                  <button
                    type="button"
                    onClick={addOption}
                    disabled={structuralLocked}
                    aria-disabled={structuralLocked}
                    className="inline-flex min-h-[32px] items-center gap-1 rounded-md px-2 py-1.5 transition-colors hover:text-card-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring disabled:opacity-40"
                  >
                    <Plus className="h-3.5 w-3.5" />
                    Option
                  </button>
                  {state.voteType === BASIC_POLL_VOTE_TYPES.MULTIPLE_CHOICE && !state.allowWriteIn ? (
                    <>
                      <span aria-hidden="true">|</span>
                      <button
                        type="button"
                        onClick={() =>
                          setState((previous) => ({
                            ...previous,
                            allowWriteIn: true,
                          }))
                        }
                        disabled={structuralLocked}
                        aria-disabled={structuralLocked}
                        className="inline-flex min-h-[32px] items-center gap-1 rounded-md px-2 py-1.5 transition-colors hover:text-card-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring disabled:opacity-40"
                      >
                        <Plus className="h-3.5 w-3.5" />
                        Other
                      </button>
                    </>
                  ) : null}
                </div>
                {state.voteType === BASIC_POLL_VOTE_TYPES.MULTIPLE_CHOICE && state.allowWriteIn ? (
                  <div className="mt-2 flex items-center justify-between rounded-lg border border-border bg-card px-3 py-2">
                    <p className="text-xs font-semibold text-muted-foreground">
                      Other (write-in)
                    </p>
                    <button
                      type="button"
                      onClick={() =>
                        setState((previous) => ({
                          ...previous,
                          allowWriteIn: false,
                        }))
                      }
                      disabled={structuralLocked}
                      aria-disabled={structuralLocked}
                      className="rounded-md border border-rose-300 px-2 py-1 text-xs text-rose-700 transition-colors hover:bg-rose-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring disabled:opacity-40 dark:border-rose-600 dark:text-rose-300 dark:hover:bg-rose-950/30"
                    >
                      Remove
                    </button>
                  </div>
                ) : null}
              </div>

              <VotePrivacySettings
                expanded={advancedSettingsExpanded}
                onExpandedChange={setAdvancedSettingsExpanded}
                voteVisibility={state.voteVisibility}
                onVoteVisibilityChange={(nextVisibility) => {
                  setState((previous) => ({
                    ...previous,
                    voteVisibility: nextVisibility,
                    hideVoterIdentities: resolveHideVoterIdentitiesForVisibility(
                      previous.hideVoterIdentities,
                      nextVisibility
                    ),
                  }));
                }}
                hideVoterIdentities={state.hideVoterIdentities}
                onHideVoterIdentitiesChange={(nextValue) =>
                  setState((previous) => ({
                    ...previous,
                    hideVoterIdentities: nextValue,
                  }))
                }
                voteAnonymization={state.voteAnonymization}
                onVoteAnonymizationChange={(nextValue) =>
                  setState((previous) => ({
                    ...previous,
                    voteAnonymization: nextValue,
                  }))
                }
                voteAnonymizationLocked={voteAnonymizationLocked}
                summaryItems={[
                  pollReminderSummary(
                    state.reminders,
                    inheritedReminderSettings,
                    "basic"
                  ),
                ]}
              >
                <PollNotificationSettings
                  idPrefix="general-poll"
                  group={activeGroup}
                  value={state.reminders}
                  inheritedValue={inheritedReminderSettings}
                  onChange={(reminders) =>
                    setState((previous) => ({ ...previous, reminders }))
                  }
                  scope="basic"
                />
              </VotePrivacySettings>
            </div>

            <DialogFooter className="mt-6">
              <Button
                type="button"
                variant="outline"
                onClick={() => onOpenChange(false)}
                className="rounded-full"
              >
                Cancel
              </Button>
              <Button
                type="button"
                onClick={handleSubmitPoll}
                disabled={saving || (!groupId && selectableGroups.length === 0)}
                className="rounded-full"
              >
                {saving ? (isEditMode ? "Saving..." : "Creating...") : isEditMode ? "Save changes" : "Create poll"}
              </Button>
            </DialogFooter>
          </>
        </DialogContent>
      </Dialog>

      <Dialog
        open={Boolean(state.noteEditor.optionId)}
        onOpenChange={(nextOpen) => {
          if (nextOpen) return;
          setState((previous) => ({
            ...previous,
            noteEditor: { optionId: null, tab: "write", value: "" },
          }));
        }}
      >
        <DialogContent className="max-w-2xl">
          <div>
            <DialogHeader>
              <DialogTitle>Option note</DialogTitle>
              <DialogDescription>
                Add markdown details for {selectedOption?.label || "this option"}.
              </DialogDescription>
            </DialogHeader>

            <div className="mt-4 space-y-3">
              <div className="flex gap-2 text-xs">
                <button
                  type="button"
                  onClick={() =>
                    setState((previous) => ({
                      ...previous,
                      noteEditor: { ...previous.noteEditor, tab: "write" },
                    }))
                  }
                  aria-pressed={state.noteEditor.tab === "write"}
                  className={`rounded-full px-3 py-1 font-semibold focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 ${
                    state.noteEditor.tab === "write"
                      ? "bg-brand-primary text-white"
                      : "text-muted-foreground hover:text-card-foreground"
                  }`}
                >
                  Write
                </button>
                <button
                  type="button"
                  onClick={() =>
                    setState((previous) => ({
                      ...previous,
                      noteEditor: { ...previous.noteEditor, tab: "preview" },
                    }))
                  }
                  aria-pressed={state.noteEditor.tab === "preview"}
                  className={`rounded-full px-3 py-1 font-semibold focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 ${
                    state.noteEditor.tab === "preview"
                      ? "bg-brand-primary text-white"
                      : "text-muted-foreground hover:text-card-foreground"
                  }`}
                >
                  Preview
                </button>
              </div>
              {state.noteEditor.tab === "write" ? (
                <>
                  <label htmlFor="poll-option-note" className="sr-only">
                    Option note
                  </label>
                  <textarea
                    id="poll-option-note"
                    value={state.noteEditor.value}
                    onChange={(event) =>
                      setState((previous) => ({
                        ...previous,
                        noteEditor: { ...previous.noteEditor, value: event.target.value },
                      }))
                    }
                    rows={6}
                    className="w-full rounded-lg border border-input bg-card px-3 py-2 text-sm text-card-foreground outline-none transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                  />
                </>
              ) : (
                <PollMarkdownContent
                  content={state.noteEditor.value}
                  fallback="_No note_"
                  className="rounded-lg border border-border bg-card px-3 py-2"
                />
              )}
            </div>

            <DialogFooter className="mt-6">
              <Button
                type="button"
                variant="outline"
                onClick={() =>
                  setState((previous) => ({
                    ...previous,
                    noteEditor: { optionId: null, tab: "write", value: "" },
                  }))
                }
                className="rounded-full"
              >
                Cancel
              </Button>
              <Button
                type="button"
                onClick={() => {
                  if (!state.noteEditor.optionId) return;
                  updateOption(state.noteEditor.optionId, { note: state.noteEditor.value });
                  setState((previous) => ({
                    ...previous,
                    noteEditor: { optionId: null, tab: "write", value: "" },
                  }));
                }}
                className="rounded-full"
              >
                Save note
              </Button>
            </DialogFooter>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}
